/*
 * File: form-file-translation.ts
 * File Created: 2026-01-22 21:12:00
 * Author: Val Liu
 */

import { Component, Prop } from "vue-property-decorator";
import { BaseFormComponent } from "@root/components/form/core/lib/base-form-component";
import { PropInnateSync } from "@root/core/vue-props";
import {
    IFormComponentAttr,
    IFormValidateResult,
} from "@root/components/form/core/lib/core";
import { StringHelper, lang } from "@root/core";
import { makeValidator } from "@root/core/decorators/make-validator";
import appConfig from "@/config/default/application";

@Component
export default class FormFileTranslation extends BaseFormComponent {
    @PropInnateSync("value", { type: [File, Array], default: undefined })
    innateValue!: File | File[] | undefined;

    @Prop({ type: String, default: ".doc,.docx,.xls,.xlsx,.ppt,.pptx" })
    contentType!: string;

    /// "100MB" / 104857600
    @Prop({ type: [Number, String], default: () => appConfig.translationFileLimit })
    limit!: string | number;

    isDragover = false;

    get hasFile() {
        return !!this.innateValue && !Array.isArray(this.innateValue);
    }

    get selectedFileName() {
        return (this.innateValue as File | undefined)?.name ?? "";
    }

    get selectedIconClass() {
        const ext = this.selectedFileName.split(".").pop()?.toLowerCase() ?? "";
        if (ext.includes("ppt")) return "fa-file-powerpoint text-danger";
        if (ext.includes("xls")) return "fa-file-excel text-success";
        if (ext.includes("doc")) return "fa-file-word text-primary";
        return "fa-file-alt text-muted";
    }

    get selectedTextClass() {
        const ext = this.selectedFileName.split(".").pop()?.toLowerCase() ?? "";
        if (ext.includes("ppt")) return "text-danger";
        if (ext.includes("xls")) return "text-success";
        if (ext.includes("doc")) return "text-primary";
        return "text-muted";
    }

    get uploadLimitLabel() {
        const limit = StringHelper.parseUnitString(this.limit);
        return limit != null
            ? StringHelper.formatUnitString(limit)
            : String(this.limit);
    }

    triggerBrowse() {
        if (this.disabled) return;
        const input = this.$refs.fileInput as HTMLInputElement | undefined;
        if (!input) return;
        input.value = "";
        input.click();
    }

    handleFileChange(event: Event) {
        const target = event.target as HTMLInputElement;
        const file = target.files ? target.files[0] : undefined;
        if (!file) return;
        this.innateValue = file;
    }

    handleDragEnter() {
        if (this.disabled) return;
        this.isDragover = true;
    }

    handleDragOver() {
        if (this.disabled) return;
        this.isDragover = true;
    }

    handleDragLeave() {
        this.isDragover = false;
    }

    handleDrop(event: DragEvent) {
        if (this.disabled) return;
        this.isDragover = false;
        const file = event.dataTransfer?.files?.[0];
        if (!file) return;
        this.innateValue = file;
    }

    /// validate size
    @makeValidator({
        validator: function (this: FormFileTranslation, o) {
            const value = o.value as File | undefined;
            if (!value) return true;
            if (this.limit === undefined) return true;

            const limit = StringHelper.parseUnitString(this.limit);
            if (limit == null) return true;

            return value.size <= limit;
        },
        message: function (this: FormFileTranslation) {
            const limit = StringHelper.parseUnitString(this.limit);
            const formatted =
                limit != null ? StringHelper.formatUnitString(limit) : null;
            return lang.translate("w_FileExceedLimit", {
                limit: formatted ?? this.limit,
            });
        },
    })
    defaultValidator() {
        return undefined;
    }

    defaultRequired(o: IFormComponentAttr): IFormValidateResult {
        const value = o.value;
        let state: NonNullable<IFormValidateResult>["state"] = undefined;
        let message: NonNullable<IFormValidateResult>["message"] = null;

        do {
            if (value == null || (Array.isArray(value) && value.length === 0)) {
                state = false;
                message = this.$_("w_RequiredField");
                break;
            }
        } while (0);
        return { state, message };
    }
}
